import React, { useState, useEffect } from 'react';
import Button from '../../../shared/components/Button';
import { formatCurrency } from '../../../shared/utils/formatCurrency';
import { formatDate } from '../../../shared/utils/formatDate';

const MemberSuccessScreen = ({ data, onComplete, navigate }) => {
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [requestingPermission, setRequestingPermission] = useState(false);

  useEffect(() => {
    // Check current notification permission
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
  }, []);

  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      setRequestingPermission(true);
      try {
        const permission = await Notification.requestPermission();
        setNotificationPermission(permission);
        
        if (permission === 'granted') {
          // Show a test notification
          new Notification('Welcome to EsuSol!', {
            body: 'You\'ll receive notifications about your group activities.',
            icon: '/favicon.ico'
          });
        }
      } catch (error) {
        console.error('Error requesting notification permission:', error);
      } finally {
        setRequestingPermission(false);
      }
    }
  };

  const frequencyLabel = {
    weekly: 'Weekly',
    'bi-weekly': 'Bi-weekly',
    monthly: 'Monthly'
  };

  return (
    <div className="flex flex-col min-h-screen px-4 py-8 bg-gradient-to-b from-neutral-100 to-neutral-50">
      {/* Success Header */}
      <div className="text-center mb-8">
        <div className="w-20 h-20 bg-success-600 rounded-full flex items-center justify-center mb-4 mx-auto">
          <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-3xl font-bold text-neutral-900 mb-2">
          Welcome to {data?.groupName || 'the Group'}! 🎉
        </h1>
        <p className="text-lg text-neutral-600">
          You're officially a member of the savings group
        </p>
      </div>

      {/* Member Details */}
      <div className="max-w-md w-full mx-auto space-y-4">
        {/* Welcome Card */}
        <div className="bg-neutral-100 rounded-lg shadow-sm border border-neutral-200 p-6">
          <h2 className="text-lg font-semibold text-neutral-900 mb-4">Your Membership Details</h2>
          
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-neutral-100">
              <span className="text-sm text-neutral-600">Member Name</span>
              <span className="text-sm font-medium text-neutral-900">{data?.name}</span>
            </div>
            
            <div className="flex justify-between items-center py-2 border-b border-neutral-100">
              <span className="text-sm text-neutral-600">Group</span>
              <span className="text-sm font-medium text-neutral-900">{data?.groupName}</span>
            </div>
            
            <div className="flex justify-between items-center py-2 border-b border-neutral-100">
              <span className="text-sm text-neutral-600">Contribution Amount</span>
              <span className="text-sm font-medium text-neutral-900">
                {formatCurrency(data?.amount || 0)}
              </span>
            </div>
            
            <div className="flex justify-between items-center py-2 border-b border-neutral-100">
              <span className="text-sm text-neutral-600">Frequency</span>
              <span className="text-sm font-medium text-neutral-900">
                {frequencyLabel[data?.frequency] || 'Monthly'}
              </span>
            </div>
            
            <div className="flex justify-between items-center py-2 border-b border-neutral-100">
              <span className="text-sm text-neutral-600">Your Position</span>
              <span className="text-sm font-medium text-primary-600">Position 3</span>
            </div>
            
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-neutral-600">First Due Date</span>
              <span className="text-sm font-medium text-primary-600">
                {formatDate(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000), 'short')}
              </span>
            </div>
          </div>
        </div>

        {/* Notification Permission */}
        <div className="bg-primary-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-primary-900 mb-2">Stay Updated</h3>
          <p className="text-xs text-primary-800 mb-3">
            Enable notifications to receive reminders about your contributions and group activities.
          </p>
          
          {notificationPermission === 'default' && (
            <Button
              onClick={requestNotificationPermission}
              variant="primary"
              size="sm"
              disabled={requestingPermission}
              className="w-full"
            >
              {requestingPermission ? 'Requesting...' : 'Enable Notifications'}
            </Button>
          )}
          
          {notificationPermission === 'granted' && (
            <div className="flex items-center space-x-2 text-xs text-success-700">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span>Notifications enabled</span>
            </div>
          )}
          
          {notificationPermission === 'denied' && (
            <div className="flex items-center space-x-2 text-xs text-neutral-600">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              <span>Notifications blocked in browser settings</span>
            </div>
          )}
        </div>

        {/* Email setup notice */}
        <div className="bg-success-50 border border-success-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-success-900 mb-1">Check Your Email</h3>
          <p className="text-xs text-success-800">
            We sent a link to <strong>{data?.email}</strong> to set up your password. Click it to activate your account, then sign in at any time to view your dashboard.
          </p>
        </div>

        {/* Next Steps */}
        <div className="bg-warning-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-warning-900 mb-2">What's Next?</h3>
          <ul className="text-xs text-warning-800 space-y-1">
            <li>• Check your email and set your password</li>
            <li>• Wait for admin to assign your hands and rotation position</li>
            <li>• You'll receive your first contribution reminder</li>
            <li>• Track your contribution history on your dashboard</li>
          </ul>
        </div>

        {/* Contact Info */}
        <div className="bg-neutral-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-neutral-900 mb-2">Your Contact Information</h3>
          <div className="text-xs text-neutral-600 space-y-1">
            <p><strong>WhatsApp:</strong> {data?.whatsappNumber}</p>
            <p><strong>SMS:</strong> {data?.smsNumber}</p>
            {data?.email && <p><strong>Email:</strong> {data.email}</p>}
            <p><strong>Notifications:</strong> {data?.notificationPreference?.replace('_', ' ')}</p>
          </div>
        </div>
      </div>

      {/* Action Button */}
      <div className="mt-8 max-w-md w-full mx-auto">
        <Button
          onClick={() => navigate('/member/dashboard')}
          variant="primary"
          size="lg"
          className="w-full text-lg py-4"
        >
          Go to My Dashboard →
        </Button>
      </div>

      {/* Security Note */}
      <div className="mt-8 text-center">
        <p className="text-xs text-neutral-500">
          Your information is secure and never shared with third parties
        </p>
      </div>
    </div>
  );
};

export default MemberSuccessScreen;
